import {DiscordContentFrameCommunicationHandler} from './DiscordContentFrameCommunicationHandler'
import {SDKRequest, DiscordCommand} from '../../../../../shared/DiscordActivityTypes'
import {uiManager} from '../../EntryHomepage'
import {uiGameManager} from '../../../../shared/scripts/EntryShared'
import {Scene} from '../../ui_html/UISceneTypes'
import {UIDLoadingScreen} from './components/UIDLoadingScreen'
import {Events} from '@discord/embedded-app-sdk'
import {UIAjaxWrapper} from '../../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../../shared/scripts/network/UIAPIEndpoints'
import {ArrayUtils} from '@colonist/utils'
import {richPresenceDetails, richPresenceStates} from '../utils/RichPresenceText'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {SDKRequestHandler} from './sdk_requests/SDKRequestHandler'
import {StorageWrapper} from '../../../../shared/scripts/StorageWrapper'
import {UINotificationSendHelper} from '../../../../shared/scripts/notifications/UINotificationSendHelper'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {DiscordSnowflake} from '@colonist/types'
import {AnalyticEventCategory, AnalyticEventType, BotShareActivityLinkAnalytics, ReferralTypeEnum, ReactivationTypeEnum, RedeemFreeAvatarAnalytics, ShareActivityLinkAnalytics} from '../../../../../shared/entity_datas/EntityDatas'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {CustomLinkUtils} from '../utils/CustomLinkUtils'
import {ViewPopupRedeemFreeAvatar} from '../../ui_popup/ViewPopupRedeemFreeAvatar'
import {ViewPopupRewardsProgram} from '../../ui_popup/ViewPopupRewardsProgram'

export class DiscordContentFrameController {
    private readonly contentFrameCommunicationHandler: DiscordContentFrameCommunicationHandler
    loadingScreen: UIDLoadingScreen
    protected sdkRequestHandler: SDKRequestHandler
    removeChangeListener: () => void

    constructor() {
        this.contentFrameCommunicationHandler = new DiscordContentFrameCommunicationHandler()
        this.sdkRequestHandler = new SDKRequestHandler(this.contentFrameCommunicationHandler)
    }

    get voiceChannelId() {
        return this.contentFrameCommunicationHandler.voiceChannelId
    }

    get serverProfile() {
        return this.contentFrameCommunicationHandler.serverProfile
    }

    get inDiscordServer() {
        return this.contentFrameCommunicationHandler.inDiscordServer
    }

    get canInviteFriends() {
        return this.contentFrameCommunicationHandler.userPermissions.canInviteFriends
    }

    get referrerId() {
        if(this.contentFrameCommunicationHandler.referrerId == 'undefined') return undefined
        return this.contentFrameCommunicationHandler.referrerId
    }

    get referralOrReactivationType() {
        if(this.contentFrameCommunicationHandler.referralTypeId == 'undefined') return undefined
        return this.contentFrameCommunicationHandler.referralTypeId
    }

    get isInitialLoad() {
        return this.contentFrameCommunicationHandler.isInitialLoad
    }

    async init(loadingScreen: UIDLoadingScreen) {
        this.loadingScreen = loadingScreen

        await this.contentFrameCommunicationHandler.waitToBeReady()

        await this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.SetActivity,
            data: {
                activity: {
                    details: ArrayUtils.randomElementFromArray(richPresenceDetails),
                    type: 0,
                    state: ArrayUtils.randomElementFromArray(richPresenceStates),
                },
            },
        })

        let firstRun = true
        const toggleFloatingWindowMode = Events.ACTIVITY_LAYOUT_MODE_UPDATE
        // eslint-disable-next-line @typescript-eslint/naming-convention
        void this.contentFrameCommunicationHandler.subscribeToDiscordEvent(toggleFloatingWindowMode, (eventData: {layout_mode: boolean}) => {
            if(firstRun) { // Mau: Discord Automatically sends this event on startup. This prevents the loading screen to hide before the game is loaded.
                firstRun = false
                return
            }
            DiscordContentFrameController.toggleFPSIfPipMode(eventData.layout_mode)
            if(eventData.layout_mode) {
                this.loadingScreen.hideSpinner()
                this.loadingScreen.showBackground()
                uiGameManager?.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.GameVisibilityUpdated)
                return
            }
            this.loadingScreen.hideBackground()
            this.loadingScreen.showSpinner()
            uiGameManager?.eventController.sendEvent(true, GameStateEnums.UIGameEventEnums.GameVisibilityUpdated)
        })

        const purchaseReceived = Events.ENTITLEMENT_CREATE
        await this.contentFrameCommunicationHandler.subscribeToDiscordEvent(purchaseReceived, async(data) => {
            await this.handlePendingPurchases()
        })

        const referrerId = this.referrerId as DiscordSnowflake | string
        const referralOrReactivationType = this.referralOrReactivationType as unknown

        if(referrerId == undefined) return
        if(referralOrReactivationType == undefined) return

        if(referrerId == 'bot') {
            const reactivationType = Number(referralOrReactivationType) as ReactivationTypeEnum

            this.removeChangeListener = ClientUserState.registerChangeListener(() => {
                const abTest = ClientUserState.abTests.REACTIVATE_DISCORD_INACTIVE_USERS

                if(abTest == undefined) return

                const botShareActivityLinkAnalytics = DiscordContentFrameController.getBotShareActivityLinkAnalytics(reactivationType, abTest)
                if(botShareActivityLinkAnalytics == undefined) return

                const eventData: AjaxDataTypes.AnalyticData = {
                    type: AnalyticEventType.ReactivationAnalytics,
                    category: AnalyticEventCategory.BotShareActivityLink,
                    floatValue: botShareActivityLinkAnalytics,
                }
                void CA.sendAnalyticEvent(eventData)

                DiscordContentFrameController.triggerSpecialAction(reactivationType, abTest)

                this.removeChangeListener()
            })

            return
        }

        const customLink = referralOrReactivationType as string

        const referralType = CustomLinkUtils.extractReferralTypeFromCustomLink(customLink) as unknown as ReferralTypeEnum

        void DiscordContentFrameController.sendReferrerValues(referrerId, referralType)
    }

    static toggleFPSIfPipMode(pipMode: boolean) {
        if(UIUtilsEjsVariables.isProduction) return
        if(uiManager == undefined) return
        if(uiManager.sceneController.scene != Scene.Game) return
        if(uiGameManager.canvasController?.fps?.div == undefined) return
        uiGameManager.canvasController.fps.div.style.display = pipMode ? 'none' : 'block'
    }

    async handlePendingPurchases() {
        await ClientUserState.waitUntilFresh

        const giftMetadataId = StorageWrapper.getGiftMetadataId() ?? undefined
        const response = await UIAjaxWrapper.postRequestWhenReady(UIAPIEndpoints.apiHandleDiscordPurchase(), undefined, {accessToken: this.contentFrameCommunicationHandler.userDiscordAccessToken, giftMetadataId: giftMetadataId})

        if(response == undefined) return

        const purchaseSuccess = response.json as boolean
        if(!purchaseSuccess) return

        StorageWrapper.clearGiftMetadataId()
        await ClientUserState.refresh()

        UINotificationSendHelper.getNotifications()
        if(uiManager.sceneController.scene == Scene.DiscordGifting) uiManager.sceneController.loadScene(Scene.DiscordStore)
    }

    sendSDKRequest(sdkRequest: SDKRequest, data?: any) {
        this.sdkRequestHandler.processSDKRequest(sdkRequest, data)
    }

    private static async sendReferrerValues(referrerId: DiscordSnowflake, referralTypeId: ReferralTypeEnum) {
        const dataToSend = {referredByUserId: referrerId, referralTypeId: referralTypeId} as unknown as AjaxDataTypes.ReferrerData
        await UIAjaxWrapper.postRequestWhenReady(UIAPIEndpoints.apiSetUserReferrer(), undefined, dataToSend)
    }

    private static getBotShareActivityLinkAnalytics(reactivationType: ReactivationTypeEnum, abTest: string | undefined): BotShareActivityLinkAnalytics | undefined {
        switch(reactivationType) {
            case ReactivationTypeEnum.BotDirectMessage24HoursAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage24HoursAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1WeekAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1WeekAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1MonthAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1MonthAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage3MonthsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage3MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage6MonthsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage6MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1YearAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1YearAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage2YearsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage2YearsAfterLastActivityLaunch
            case ReactivationTypeEnum.ABTestStart:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupAStart
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupBStart
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupCStart
                return undefined
            case ReactivationTypeEnum.ABTestNow:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupANow
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupBNow
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupCNow
                return undefined
            case ReactivationTypeEnum.ABTest2D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA2D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB2D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC2D
                return undefined
            case ReactivationTypeEnum.ABTest4D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA4D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB4D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC4D
                return undefined
            case ReactivationTypeEnum.ABTest7D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA7D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB7D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC7D
                return undefined
            case ReactivationTypeEnum.ABTest15D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA15D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB15D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC15D
                return undefined
            default:
                return undefined
        }
    }

    private static triggerSpecialAction(reactivationType: ReactivationTypeEnum, abTest: string | undefined) {
        if([ReactivationTypeEnum.ABTestStart, ReactivationTypeEnum.ABTestNow, ReactivationTypeEnum.ABTest2D, ReactivationTypeEnum.ABTest4D, ReactivationTypeEnum.ABTest7D, ReactivationTypeEnum.ABTest15D].includes(reactivationType)) {
            switch(abTest) {
                case 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE':
                    break
                case 'GROUP_B_PROMOTE_REFERRAL_PROGRAM':
                    new ViewPopupRewardsProgram(ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPopupTriggered, ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramSuccessfullyShared)

                    const eventDataRewardsProgram: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReferralAnalytics,
                        category: AnalyticEventCategory.ShareActivityLink,
                        floatValue: ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPrePopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRewardsProgram)
                    break
                case 'GROUP_C_GET_FREE_AVATAR':
                    new ViewPopupRedeemFreeAvatar()

                    const eventDataRedeemFreeAvatar: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReactivationAnalytics,
                        category: AnalyticEventCategory.ABTestRedeemFreeAvatar,
                        floatValue: RedeemFreeAvatarAnalytics.ABTestRedeemFreeAvatarPopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRedeemFreeAvatar)
                    break
                default: break
            }
        }
    }
}
